/**
 * The sync engine: push, then pull, on a loop.
 *
 * Push first, always. If we pulled first, the server's older copy of a row we
 * have just edited locally would land on top of the edit and the user would
 * watch their change flicker away before it was ever sent.
 *
 * Triggers, in order of how often they actually fire in a factory:
 *   - reconnect (a phone walking out of a dead zone)
 *   - tab/app becoming visible again
 *   - a local write
 *   - a slow poll, as a backstop
 */
import { SYNC_TABLE_MAP, db, getMeta, META_KEYS, setMeta } from '../db/schema.js';
import { isNetworkError, syncPull, syncPush } from '../api/client.js';
import { claimBatch, deferBatch, refreshCounters, settleBatch } from './outbox.js';
import { patchStatus, syncStatus } from './status.js';
import { SYNC_PUSH_BATCH_SIZE, type SyncPullResponse } from '@mouldtrack/shared';

const POLL_INTERVAL_MS = 60_000;

let running = false;
let timer: ReturnType<typeof setInterval> | null = null;
/** Single-flight: overlapping cycles would fight over the cursor. */
let inFlight: Promise<void> | null = null;
let disposers: Array<() => void> = [];

// --- pull ---

async function applyPull(response: SyncPullResponse): Promise<void> {
  const tables = Object.keys(response.changes) as Array<keyof typeof SYNC_TABLE_MAP>;
  if (tables.length === 0) return;

  // Rows with an unsent local edit must survive the pull, or the user's work
  // disappears the moment someone else touches the catalog.
  const pendingIds = new Set(
    (await db.outbox.where('status').anyOf('queued', 'sending').toArray()).map(
      (m) => `${m.table}:${m.id}`,
    ),
  );

  const dexieTables = tables.map((t) => db.table(SYNC_TABLE_MAP[t]));
  await db.transaction('rw', dexieTables, async () => {
    for (const table of tables) {
      const rows = response.changes[table];
      if (!rows?.length) continue;

      const target = db.table(SYNC_TABLE_MAP[table]);
      const tombstones: string[] = [];
      const upserts: Array<Record<string, unknown>> = [];

      for (const row of rows) {
        if (pendingIds.has(`${table}:${row.id}`)) continue;
        if (row.deletedAt) tombstones.push(row.id);
        else upserts.push({ ...row, _pending: 0 });
      }

      if (upserts.length) await target.bulkPut(upserts);
      // A tombstone means "gone on the server". Keeping it locally would let the
      // row reappear in dropdowns forever.
      if (tombstones.length) await target.bulkDelete(tombstones);
    }
  });
}

async function pull(): Promise<void> {
  const bootstrapped = syncStatus.get().bootstrapped;
  patchStatus({ phase: bootstrapped ? 'pulling' : 'bootstrapping' });

  // Drain every page before declaring the pull done, or the cursor advances past
  // rows we never stored.
  let guard = 0;
  for (;;) {
    const since = await getMeta<string>(META_KEYS.syncCursor);
    const response = await syncPull(since);
    await applyPull(response);
    await setMeta(META_KEYS.syncCursor, response.cursor);

    if (!response.hasMore) break;
    if ((guard += 1) > 50) break; // pathological server; stop rather than spin
  }

  const now = new Date().toISOString();
  await setMeta(META_KEYS.lastPullAt, now);
  await setMeta(META_KEYS.bootstrapped, true);
  patchStatus({ lastPullAt: now, bootstrapped: true, lastError: null });
}

// --- push ---

async function push(): Promise<void> {
  for (;;) {
    const batch = await claimBatch();
    if (batch.length === 0) return;

    patchStatus({ phase: 'pushing' });
    try {
      const response = await syncPush({
        mutations: batch.map(({ seq: _seq, status: _s, attempts: _a, lastError: _e, nextAttemptAt: _n, ...m }) => m),
      });
      await settleBatch(batch, response.results);
      await setMeta(META_KEYS.lastPushAt, new Date().toISOString());
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Push failed';
      await deferBatch(batch, message);
      throw error;
    } finally {
      await refreshCounters();
    }

    // Loop until a claim comes back empty. "Fewer than a full batch" no longer
    // means drained: a batch can close early on size with more queued behind it.
    // A batch whose every entry was deferred or failed stops the loop instead.
    if (batch.length < SYNC_PUSH_BATCH_SIZE && !(await moreToSend(batch))) return;
  }
}

/** Anything still queued and due that was not in the batch just sent? */
async function moreToSend(sent: Array<{ seq?: number }>): Promise<boolean> {
  const sentSeqs = new Set(sent.map((e) => e.seq));
  const now = new Date().toISOString();
  const queued = await db.outbox.where('status').equals('queued').toArray();
  return queued.some((e) => !sentSeqs.has(e.seq) && (!e.nextAttemptAt || e.nextAttemptAt <= now));
}

// --- cycle ---

/** Run one push+pull cycle. Safe to call from anywhere; overlapping calls share one run. */
export function syncNow(): Promise<void> {
  if (inFlight) return inFlight;

  inFlight = (async () => {
    if (!navigator.onLine) {
      patchStatus({ online: false, phase: 'idle' });
      await refreshCounters();
      return;
    }

    patchStatus({ online: true });
    try {
      await push();
      await pull();
      patchStatus({ phase: 'idle', lastError: null });
    } catch (error) {
      if (isNetworkError(error)) {
        // Not an error the user can act on - they are simply offline.
        patchStatus({ phase: 'idle', online: false });
      } else {
        patchStatus({
          phase: 'error',
          lastError: error instanceof Error ? error.message : 'Sync failed',
        });
      }
    } finally {
      await refreshCounters();
      inFlight = null;
    }
  })();

  return inFlight;
}

/** Nudge the engine after a local write, without blocking the UI on it. */
export function requestSync(): void {
  if (!running) return;
  void syncNow();
}

export async function startSync(): Promise<void> {
  if (running) return;
  running = true;

  patchStatus({
    bootstrapped: (await getMeta<boolean>(META_KEYS.bootstrapped)) ?? false,
    lastPullAt: (await getMeta<string>(META_KEYS.lastPullAt)) ?? null,
    online: navigator.onLine,
  });
  await refreshCounters();

  const onOnline = () => {
    patchStatus({ online: true });
    void syncNow();
  };
  const onOffline = () => patchStatus({ online: false });
  const onVisible = () => {
    if (document.visibilityState === 'visible') void syncNow();
  };

  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  document.addEventListener('visibilitychange', onVisible);
  timer = setInterval(() => void syncNow(), POLL_INTERVAL_MS);

  disposers = [
    () => window.removeEventListener('online', onOnline),
    () => window.removeEventListener('offline', onOffline),
    () => document.removeEventListener('visibilitychange', onVisible),
  ];

  void syncNow();
}

export function stopSync(): void {
  running = false;
  if (timer) clearInterval(timer);
  timer = null;
  for (const d of disposers) d();
  disposers = [];
}

/** Force a full re-replication - the "something looks wrong" escape hatch. */
export async function resetAndResync(): Promise<void> {
  await db.clearReplicatedData();
  await setMeta(META_KEYS.syncCursor, null);
  await setMeta(META_KEYS.bootstrapped, false);
  patchStatus({ bootstrapped: false });
  await syncNow();
}
